import { useState, useMemo, useEffect, startTransition } from 'react'
import { Order, Product } from '@/types/commerce'
import { ORDER_STATUSES } from '@/constants/commerce'

/**
 * Constants defining which order statuses are considered active or unredeemable
 * for special offer eligibility
 */
const ACTIVE_ORDER_STATUSES: number[] = [ORDER_STATUSES.ACTIVE]
const UNREDEEMABLE_ORDER_STATUSES: number[] = [
  ORDER_STATUSES.PAUSED,
  ORDER_STATUSES.ACTIVE,
  ORDER_STATUSES.PAST_DUE,
  ORDER_STATUSES.TRIAL,
  ORDER_STATUSES.PENDING
]

interface SpecialOfferOptions {
  /** User's current orders */
  orders?: Order[]
  /** Available products */
  products?: Product[]
  /** Whether the hook should perform calculations */
  enabled?: boolean
}

/**
 * Hook for calculating special offer eligibility based on user's orders and available products
 */
export const useSpecialOffer = ({
  orders = [],
  products = [],
  enabled = true
}: SpecialOfferOptions) => {
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Calculate eligible add-on products based on user's orders and available products
   */
  const addOnProducts = useMemo(() => {
    if (!enabled) return []
    if (!orders?.length || !products?.length) return []

    try {
      const availableProductIds = new Set(products.map(p => p?._id).filter(Boolean))
      const potentialAddOnProducts: (Product & { addOn: any })[] = []

      // Identify potential add-on products from active orders
      orders.forEach(order => {
        if (!ACTIVE_ORDER_STATUSES.includes(order?.status)) return

        // Process add-ons from the order
        const addOns = order?.product?.gateway?.addOns || []
        addOns.forEach((addOnInOrder: any) => {
          if (!addOnInOrder?._id || !availableProductIds.has(addOnInOrder._id)) return

          const matchingProduct = products.find(p => p._id === addOnInOrder._id)
          if (matchingProduct) {
            potentialAddOnProducts.push({
              ...matchingProduct,
              addOn: addOnInOrder
            })
          }
        })
      })

      if (potentialAddOnProducts.length === 0) return []

      // Check if user already has any of these add-ons in unredeemable orders
      const offeredAddOnProductIds = new Set(potentialAddOnProducts.map(p => p._id))
      const userHasInvalidatingOrder = orders.some(order => {
        const isUnredeemable = UNREDEEMABLE_ORDER_STATUSES.includes(order?.status)
        const orderMainProductIsOfferedAddOn = order?.product?._id &&
          offeredAddOnProductIds.has(order.product._id)

        return isUnredeemable && orderMainProductIsOfferedAddOn
      })

      if (userHasInvalidatingOrder) return []

      // Return unique products by _id
      return Array.from(
        new Map(potentialAddOnProducts.map(p => [p._id, p])).values()
      )
    } catch (error) {
      // Handle any unexpected errors in calculation
      console.error('Error calculating add-on products:', error)
      return []
    }
  }, [orders, products, enabled])

  useEffect(() => {
    startTransition(() => setIsLoading(false))
  }, [orders, products, enabled])

  // Get first product for convenience
  const firstAddOnProduct = addOnProducts[0]

  // Check if we have eligible products
  const hasSpecialOffer = addOnProducts.length > 0

  return {
    isLoading,
    hasSpecialOffer,
    addOnProducts,
    product: firstAddOnProduct,
    addOn: firstAddOnProduct?.addOn
  }
}